import { makeAutoObservable, runInAction } from 'mobx';
import { IRootStore, WordStatus, ITextEntity, IReadingSession, IReadingWord, IReadingSessionActions, IReadingSessionStats, createReadingSessionFromTextEntity } from '@shared/types';

/**
 * 📚 READING SESSION STORE - Data Manager לנתוני הקריאה
 * 
 * **תפקיד ראשי:** Data Layer שמנהל את מצב הקריאה והסטטיסטיקות
 * 
 * **אחריות:**
 * - ניהול מופע IReadingSession (מילים, סטטוסים, התקדמות)
 * - חישוב סטטיסטיקות בזמן אמת (דיוק, מילים נכונות/שגויות)
 * - פעולות CRUD על נתוני הקריאה (עדכון סטטוס מילה, מחיקה, איפוס)
 * - מעקב אחר המילה הנוכחית והמילה הבאה
 * - ניהול מצב השגיאות והניסיונות החוזרים
 * - המרה מ-TextEntity ל-ReadingSession
 * 
 * **מתקשר עם:**
 * - אף סטור אחר - עצמאי לחלוטין (Pure Data Store)
 * - מקבל קריאות מ-speechRecognitionStore בלבד
 * 
 * **Pattern:** Repository + State Manager - מנהל נתונים ומצב פנימי
 * 
 * **הערה:** Store זה הוא Pure Data Layer ולא אמור לדעת על UI או Speech Recognition
 */
class ReadingSessionStore implements IReadingSessionActions {
    rootStore: IRootStore;

    // המופע הראשי של הקריאה
    readingSession: IReadingSession | null = null;

    // קונסטנטים
    private static readonly MAX_ATTEMPTS = 3;

    constructor(rootStore: IRootStore) {
        makeAutoObservable(this);
        this.rootStore = rootStore;
    }

    // ---- Getters ----

    /**
     * מחזיר את המילה הנוכחית שצריכה להיקרא
     */
    get currentWord(): IReadingWord | null {
        if (!this.readingSession || this.readingSession.currentWordIndex >= this.readingSession.words.length) {
            return null;
        }
        return this.readingSession.words[this.readingSession.currentWordIndex];
    }

    /**
     * מחזיר האם הקריאה הושלמה
     */
    get isCompleted(): boolean {
        return this.readingSession?.isCompleted || false;
    }

    /**
     * מחזיר את הסטטיסטיקות של הקריאה
     */
    get stats(): IReadingSessionStats {
        if (!this.readingSession) {
            return {
                correctWords: 0,
                failedWords: 0,
                pendingWords: 0,
                accuracyPercentage: 0,
            };
        }

        const count = (status: WordStatus) =>
            this.readingSession!.words.filter((w: IReadingWord) => w.status === status).length;
        const nearWords = count(WordStatus.Near);
        const coarticulatedWords = count(WordStatus.Coarticulated);
        // A near miss and a swallowed word are both read words: amber on the
        // screen, a note in the log, and no penalty in the score.
        const correctWords = count(WordStatus.Correct) + nearWords + coarticulatedWords;
        const failedWords = count(WordStatus.Mistake);
        const pendingWords = count(WordStatus.Pending);
        const totalWords = this.readingSession.words.length;

        console.log(correctWords,'||',failedWords,'||',pendingWords,'||',totalWords)

        return {
            correctWords,
            failedWords,
            pendingWords,
            nearWords,
            coarticulatedWords,
            accuracyPercentage: totalWords > 0 ? Math.round((correctWords / totalWords) * 100) : 0,
        };
    }

    /**
     * מחזיר את הטקסט שנקרא עד כה
     */
    get spokenText(): string {
        return this.readingSession?.spokenText || '';
    }

    // ---- Actions מממשק IReadingSessionActions ----

    /**
     * יצירת session חדש מ-textEntity
     */
    initializeFromTextEntity = (textEntity: ITextEntity): void => {
        runInAction(() => {
            this.readingSession = createReadingSessionFromTextEntity(textEntity);
        });
    };

    /**
     * עדכון סטטוס מילה לאחר ניסיון קריאה
     */
    updateWordStatus = (wordIndex: number, spokenWord: string, status: WordStatus): void => {
        // console.log("== 4 ==>", wordIndex, "||", this.readingSession.currentWordIndex)
        if (!this.readingSession || wordIndex !== this.readingSession.currentWordIndex) {
            return;
        }

        runInAction(() => {
            const currentWord = this.readingSession!.words[wordIndex];

            if (status === WordStatus.Correct || status === WordStatus.Near || status === WordStatus.Coarticulated) {
                // מילה שנקראה, במדויק או כמעט - נעבור למילה הבאה
                currentWord.status = status;
                currentWord.isCurrentWord = false;
                
                // איפוס מצב השגיאה אם היה
                this.readingSession!.currentWordErrorCount = 0;
                this.readingSession!.currentAttemptedWord = '';
                
                this.advanceToNextWord();
            } else if (status === WordStatus.Mistake  || status === WordStatus.Missing) {
                currentWord.status = status;
                currentWord.isCurrentWord = false;
                this.markWordAsFailed(wordIndex);

                // this.advanceToNextWord();
                // מילה שגויה או misplaced - נסמן מיד באדום ונוסיף לרשימת הניסיונות
                // currentWord.status = WordStatus.Mistake;
                // currentWord.attemptedWords.push(spokenWord);
                // this.readingSession!.currentWordErrorCount++;
                // this.readingSession!.errorAttemptId++;
                // this.readingSession!.currentAttemptedWord = spokenWord;

                // // אם הגענו למקסימום ניסיונות - נעבור למילה הבאה
                // if (this.readingSession!.currentWordErrorCount >= ReadingSessionStore.MAX_ATTEMPTS) {
                //     this.markWordAsFailed(wordIndex);
                // } else {
                //     // עדכון מזהה האנימציה
                //     currentWord.errorAttemptId = this.readingSession!.errorAttemptId;
                // }
            }
            // || status === WordStatus.Include
        });
    };

    /**
     * מעבר למילה הבאה לאחר הצלחה
     */
    advanceToNextWord = (): void => {
        if (!this.readingSession) return;

        runInAction(() => {
            const currentWord = this.readingSession!.words[this.readingSession!.currentWordIndex];

            // הוספת המילה לטקסט שנאמר
            if (this.readingSession!.spokenText) {
                this.readingSession!.spokenText += ' ';
            }
            this.readingSession!.spokenText += currentWord.cleanWord;

            // מעבר למילה הבאה
            this.readingSession!.currentWordIndex++;
            this.readingSession!.currentWordErrorCount = 0;
            this.readingSession!.currentAttemptedWord = '';

            // בדיקה אם הקריאה הושלמה
            if (this.readingSession!.currentWordIndex >= this.readingSession!.words.length) {
                this.readingSession!.isCompleted = true;
            } else {
                // סימון המילה הבאה כנוכחית
                this.readingSession!.words[this.readingSession!.currentWordIndex].isCurrentWord = true;
            }
        });
    };

    /**
     * סימון מילה כנכשלת לאחר 3 ניסיונות
     */
    markWordAsFailed = (wordIndex: number): void => {
        if (!this.readingSession || wordIndex !== this.readingSession.currentWordIndex) {
            return;
        }

        runInAction(() => {
            const currentWord = this.readingSession!.words[wordIndex];
            currentWord.status = WordStatus.Mistake;
            currentWord.isCurrentWord = false;

            // מעבר למילה הבאה
            this.readingSession!.currentWordIndex++;
            this.readingSession!.currentWordErrorCount = 0;
            this.readingSession!.currentAttemptedWord = '';

            // בדיקה אם הקריאה הושלמה
            if (this.readingSession!.currentWordIndex >= this.readingSession!.words.length) {
                this.readingSession!.isCompleted = true;
            } else {
                // סימון המילה הבאה כנוכחית
                this.readingSession!.words[this.readingSession!.currentWordIndex].isCurrentWord = true;
            }
        });
    };

    /**
     * איפוס מילה אחרונה
     */
    removeLastWord = (): void => {
        if (!this.readingSession || this.readingSession.currentWordIndex === 0) {
            return;
        }

        runInAction(() => {
            console.log('📝 removeLastWord - Before:', {
                currentWordIndex: this.readingSession!.currentWordIndex,
                spokenText: this.readingSession!.spokenText
            });

            // חזרה למילה הקודמת
            if (this.readingSession!.currentWordIndex < this.readingSession!.words.length) {
                this.readingSession!.words[this.readingSession!.currentWordIndex].isCurrentWord = false;
            }

            this.readingSession!.currentWordIndex--;
            const targetWordIndex = this.readingSession!.currentWordIndex;
            const targetWord = this.readingSession!.words[targetWordIndex];

            // איפוס המילה שאנחנו חוזרים אליה
            targetWord.status = WordStatus.Pending;
            targetWord.isCurrentWord = true;
            targetWord.attemptedWords = [];
            targetWord.errorAttemptId = undefined;

            // איפוס כל המילים שאחרי המילה הנוכחית (מילים שעדיין לא הגענו אליהן)
            for (let i = targetWordIndex + 1; i < this.readingSession!.words.length; i++) {
                const futureWord = this.readingSession!.words[i];
                futureWord.status = WordStatus.Pending;
                futureWord.isCurrentWord = false;
                futureWord.attemptedWords = [];
                futureWord.errorAttemptId = undefined;
            }

            // עדכון הטקסט שנאמר - רק המילים שבאמת נקראו נכון
            const correctlyReadWords: string[] = [];
            for (let i = 0; i < targetWordIndex; i++) {
                const word = this.readingSession!.words[i];
                if (word.status === WordStatus.Correct || word.status === WordStatus.Near || word.status === WordStatus.Coarticulated) {
                    correctlyReadWords.push(word.cleanWord);
                }
            }
            this.readingSession!.spokenText = correctlyReadWords.join(' ');

            // איפוס מצב השגיאה
            this.readingSession!.currentWordErrorCount = 0;
            this.readingSession!.currentAttemptedWord = '';
            this.readingSession!.isCompleted = false;

            console.log('📝 removeLastWord - After:', {
                currentWordIndex: this.readingSession!.currentWordIndex,
                spokenText: this.readingSession!.spokenText,
                targetWordStatus: targetWord.status
            });
        });
    };

    /**
     * איפוס כל הטקסט
     */
    resetSession = (): void => {
        if (!this.readingSession) return;

        runInAction(() => {
            // איפוס כל המילים
            this.readingSession!.words.forEach((word: IReadingWord, index: number) => {
                word.status = WordStatus.Pending;
                word.isCurrentWord = index === 0;
                word.attemptedWords = [];
                word.errorAttemptId = undefined;
            });

            // איפוס מצב ה-session
            this.readingSession!.currentWordIndex = 0;
            this.readingSession!.spokenText = '';
            this.readingSession!.isCompleted = false;
            this.readingSession!.currentWordErrorCount = 0;
            this.readingSession!.errorAttemptId = 0;
            this.readingSession!.currentAttemptedWord = '';
        });
    };

    // ---- פעולות נוספות ----

    /**
     * מחזיר את כל המילים
     */
    get allWords(): IReadingWord[] {
        return this.readingSession?.words || [];
    }

    /**
     * מחזיר האם יש session פעיל
     */
    get hasActiveSession(): boolean {
        return this.readingSession !== null;
    }
}

export default ReadingSessionStore; 